/**
 * InsightsHomepage — the composition surface where every WATCH report lands.
 *
 * ACT-then-WATCH slice (live Site Variance + live E2 ScheduleMisalignment
 * per-site cards). This component is purely presentational: it renders
 * from serializable `ActionCardSlot[]` / `WatchTile[]` props and forwards
 * the Review click-in — a BFF loader (or the schedule-misalignment detail
 * route) drops in at the parent route with zero component changes here.
 *
 * ACT = cerulean-free action cards ("what to do" — ranked by impact,
 * highest gets the pinned "Top action" treatment; empty hero renders the
 * all-clear card instead of nothing). WATCH = cerulean, per-tile click-in
 * ("what's going on" — observations + redone platform reports). Each WATCH
 * tile is one content domain; new tiles land by registration in
 * `watch-zone`, never by editing this page.
 */
import type React from "react"

import { Box } from "@mui/material"
import { styled } from "@mui/material/styles"

import { ActionCardRow } from "./action-cards"
import type { ActionCardSlot, WatchTile } from "./insights-homepage.types"
import { PortfolioImpactHero } from "./portfolio-impact-hero"
import { WatchZone } from "./watch-zone"

const Page = styled(Box)(({ theme }) => ({
  maxWidth: 1304,
  margin: "0 auto",
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3.5),
}))

export interface InsightsHomepageProps {
  /** ACT zone — ranked action cards; empty renders the all-clear hero. */
  hero: readonly ActionCardSlot[]
  /** Review click-in for an ACT card, keyed by `ActionCardSlot.id`. */
  onReviewAction: (id: string) => void
  /** Click-in for the trailing "Explore all recommended actions" card. */
  onExploreAll: () => void
  /** ACT feed still in-flight — hold the loading hero, not all-clear. */
  heroLoading?: boolean
  /** ACT feed failed to load — render the 404 page, not all-clear. */
  heroError?: boolean
  /** WATCH zone — report-rack tiles; empty renders nothing. */
  watch: readonly WatchTile[]
  /** Analysis window under consideration (e.g. "the trailing 12 months"). */
  periodLabel?: string
}

export const InsightsHomepage: React.FC<InsightsHomepageProps> = ({
  hero,
  onReviewAction,
  onExploreAll,
  heroLoading = false,
  heroError = false,
  watch,
  periodLabel,
}) => (
  <Page>
    {/* Money headline — only with real open actions (loading/error/all-clear
        states are carried by the ACT row's own hero below). */}
    {!heroLoading && !heroError && hero.length > 0 ? (
      <PortfolioImpactHero cards={hero} periodLabel={periodLabel} />
    ) : null}
    <ActionCardRow
      cards={hero}
      error={heroError}
      loading={heroLoading}
      onExploreAll={onExploreAll}
      onReview={onReviewAction}
    />
    <WatchZone tiles={watch} />
  </Page>
)
